<template>
  <div class="min-h-screen bg-gray-50 dark:bg-neutral-900">
    <Breadcrumb :items="[{ title: 'İletişim', path: '/contact' }]" />
    
    <!-- Hero Section -->
    <div class="relative h-[400px] overflow-hidden">
      <div class="absolute inset-0">
        <img src="/assets/images/contact/office.jpg" alt="Teknopark Office" class="w-full h-full object-cover" />
        <div class="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/40"></div>
      </div>
      <div class="relative container mx-auto px-4 h-full flex items-center">
        <div class="max-w-2xl text-white">
          <h1 class="text-4xl md:text-5xl font-bold mb-4">{{ t('contact.title') }}</h1>
          <p class="text-lg md:text-xl opacity-90">{{ t('contact.subtitle') }}</p>
        </div>
      </div>
    </div>

    <!-- Contact Section -->
    <div class="py-16">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <!-- Contact Form -->
          <div class="bg-white dark:bg-neutral-800 rounded-2xl p-8 shadow-lg">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">{{ t('contact.formTitle') }}</h2>
            <form @submit.prevent="handleSubmit" class="space-y-6">
              <!-- İsim -->
              <div class="form-group">
                <label class="form-label">{{ t('contact.name') }}</label>
                <input type="text" 
                       v-model="form.name" 
                       :class="[
                         'form-input',
                         errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                       ]"
                       @blur="validateField('name')"
                       :placeholder="t('contact.name')"
                       required />
                <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
              </div>

              <!-- Email -->
              <div class="form-group">
                <label class="form-label">{{ t('contact.email') }}</label>
                <input type="email" 
                       v-model="form.email" 
                       :class="[
                         'form-input',
                         errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                       ]"
                       @blur="validateField('email')"
                       placeholder="ornek@email.com"
                       required />
                <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
              </div>

              <!-- Telefon -->
              <div class="form-group">
                <label class="form-label">{{ t('contact.phone') }}</label>
                <input type="tel" 
                       v-model="form.phone" 
                       :class="[
                         'form-input',
                         errors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                       ]"
                       @blur="validateField('phone')"
                       placeholder="+90 5XX XXX XX XX" />
                <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
                <span class="text-xs text-gray-500 dark:text-gray-400 mt-1 block">{{ t('contact.phoneOptional') }}</span>
              </div>

              <!-- Departman -->
              <div class="form-group" ref="departmentDropdownRef">
                <label class="form-label">{{ t('contact.department') }}</label>
                <div class="relative">
                  <button type="button"
                    @click="isOpenDepartment = !isOpenDepartment"
                    :class="[
                      'form-select',
                      errors.department ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                    ]">
                    <span class="block truncate">{{ form.department ? departmentOptions.find(d => d.value === form.department)?.label : t('contact.selectDepartment') }}</span>
                    <span class="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                      <svg class="w-4 h-4 text-gray-500 transition-transform duration-300" :class="{ 'rotate-180': isOpenDepartment }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </button>
                  <span v-if="errors.department" class="error-message">{{ errors.department }}</span>
                  
                  <div v-if="isOpenDepartment" 
                       class="dropdown-menu">
                    <div class="max-h-60 overflow-y-auto custom-scrollbar">
                      <button type="button"
                              v-for="option in departmentOptions" 
                              :key="option.value"
                              @click="form.department = option.value; isOpenDepartment = false"
                              class="dropdown-item"
                              :class="form.department === option.value ? 'text-primary font-medium bg-primary/5' : ''">
                        {{ option.label }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Konu -->
              <div class="form-group">
                <label class="form-label">{{ t('contact.subject') }}</label>
                <input type="text" 
                       v-model="form.subject" 
                       :class="[
                         'form-input',
                         errors.subject ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                       ]"
                       @blur="validateField('subject')"
                       :placeholder="t('contact.subject')"
                       required />
                <span v-if="errors.subject" class="error-message">{{ errors.subject }}</span>
              </div>

              <!-- Mesaj -->
              <div class="form-group">
                <label class="form-label">{{ t('contact.message') }}</label>
                <textarea v-model="form.message" 
                         rows="4"
                         :class="[
                           'form-input resize-none',
                           errors.message ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                         ]"
                         @blur="validateField('message')"
                         :placeholder="t('contact.message')"
                         required></textarea>
                <span v-if="errors.message" class="error-message">{{ errors.message }}</span>
              </div>

              <!-- Dosya Yükleme -->
              <div class="form-group">
                <input type="file" 
                       @change="handleFileUpload" 
                       class="hidden" 
                       ref="fileInput"
                       accept=".pdf,.doc,.docx,.jpg,.png" />
                <button type="button" 
                        @click="() => fileInput?.click()"
                        class="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-primary transition-colors">
                  <i class="fas fa-paperclip mr-2"></i>
                  {{ t('contact.attachFile') }}
                </button>
                <div v-if="form.file" class="mt-2 text-sm text-primary">
                  {{ form.file.name }}
                  <button @click="removeFile" class="ml-2 text-red-500 hover:text-red-600">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>

              <!-- Submit Button -->
              <button type="submit" 
                      :disabled="isLoading"
                      class="w-full bg-primary text-white py-3 rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center group disabled:opacity-70 disabled:cursor-not-allowed">
                <span v-if="isLoading">{{ t('contact.sending') }}</span>
                <span v-else>{{ t('contact.send') }}</span>
                <i v-if="!isLoading" class="fas fa-paper-plane ml-2 group-hover:translate-x-1 transition-transform"></i>
                <i v-else class="fas fa-spinner fa-spin ml-2"></i>
              </button>
            </form>
          </div>

          <!-- Contact Info -->
          <div class="space-y-8">
            <!-- Info Cards -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Address -->
              <div class="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg">
                <div class="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <i class="fas fa-map-marker-alt text-primary text-xl"></i>
                </div>
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{{ t('contact.address') }}</h3>
                <p class="text-gray-600 dark:text-gray-400">
                  {{ contactInfo.address }}
                </p>
              </div>

              <!-- Phone & Email -->
              <div class="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg">
                <div class="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <i class="fas fa-phone-alt text-primary text-xl"></i>
                </div>
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{{ t('contact.contactInfo') }}</h3>
                <p class="text-gray-600 dark:text-gray-400">
                  <a :href="`tel:${contactInfo.phone}`" class="hover:text-primary transition-colors">{{ contactInfo.phone }}</a><br>
                  <a :href="`mailto:${contactInfo.email}`" class="hover:text-primary transition-colors">{{ contactInfo.email }}</a>
                </p>
              </div>
            </div>

            <!-- Working Hours -->
            <div class="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg">
              <div class="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <i class="fas fa-clock text-primary text-xl"></i>
              </div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{{ t('contact.workingHours') }}</h3>
              <ul class="space-y-2 text-gray-600 dark:text-gray-400">
                <li>
                  <div class="flex justify-between">
                    <span>{{ t('contact.daysOfWeek.mondayToFriday') }}</span>
                    <span>09:00 - 18:00</span>
                  </div>
                </li>
                <li>
                  <div class="flex justify-between">
                    <span>{{ t('contact.daysOfWeek.saturday') }}</span>
                    <span>10:00 - 14:00</span>
                  </div>
                </li>
                <li>
                  <div class="flex justify-between">
                    <span>{{ t('contact.daysOfWeek.sunday') }}</span>
                    <span>{{ t('contact.status.closed') }}</span>
                  </div>
                </li>
              </ul>
            </div>

            <!-- Social Media -->
            <div class="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg">
              <div class="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <i class="fas fa-share-alt text-primary text-xl"></i>
              </div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Sosyal Medya</h3>
              <div class="flex space-x-4">
                <a v-if="socialMedia.facebook" :href="socialMedia.facebook" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-xl bg-gray-100 dark:bg-neutral-700 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary hover:text-white transition-colors">
                  <i class="fab fa-facebook-f"></i>
                </a>
                <a v-if="socialMedia.twitter" :href="socialMedia.twitter" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-xl bg-gray-100 dark:bg-neutral-700 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary hover:text-white transition-colors">
                  <i class="fab fa-twitter"></i>
                </a>
                <a v-if="socialMedia.linkedin" :href="socialMedia.linkedin" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-xl bg-gray-100 dark:bg-neutral-700 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary hover:text-white transition-colors">
                  <i class="fab fa-linkedin-in"></i>
                </a>
                <a v-if="socialMedia.instagram" :href="socialMedia.instagram" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-xl bg-gray-100 dark:bg-neutral-700 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary hover:text-white transition-colors">
                  <i class="fab fa-instagram"></i>
                </a>
                <a v-if="socialMedia.whatsapp" :href="`https://wa.me/${socialMedia.whatsapp.replace(/[^0-9]/g, '')}`" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-xl bg-gray-100 dark:bg-neutral-700 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary hover:text-white transition-colors">
                  <i class="fab fa-whatsapp"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Ulaşım Bilgileri Bölümü -->
    <div class="py-16 bg-gray-50 dark:bg-neutral-800">
      <div class="container mx-auto px-4">
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold text-gray-900 dark:text-white mb-4">{{ t('contact.directions.title') }}</h2>
          <p class="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">{{ t('contact.directions.subtitle') }}</p>
        </div>
        
        <div class="bg-white dark:bg-neutral-700 rounded-2xl p-8 shadow-xl">
          <div class="accordion" id="ulasimakordionu">
            <!-- Şehir Merkezi Yönünden -->
            <div class="mb-4">
              <button 
                class="w-full px-5 py-3 flex justify-between items-center rounded-xl bg-gray-100 dark:bg-neutral-600 text-gray-700 dark:text-white font-medium hover:bg-primary/10 dark:hover:bg-primary/20 transition-all"
                @click="toggleAccordion('accordion1')"
                type="button">
                <span>{{ t('contact.directions.fromCityCenter') }}</span>
                <i class="fas fa-chevron-down transition-transform"
                   :class="{'rotate-180': openAccordion === 'accordion1'}"></i>
              </button>
              <div 
                class="overflow-hidden transition-all duration-300 bg-white dark:bg-neutral-700 rounded-b-xl"
                :class="{'max-h-0 mt-0': openAccordion !== 'accordion1', 'max-h-[500px] mt-2': openAccordion === 'accordion1'}">
                <ul class="p-5 space-y-2 text-gray-600 dark:text-gray-300 list-disc pl-5">
                  <li>{{ t('contact.directions.directions.cityCenter.0') }}</li>
                  <li>{{ t('contact.directions.directions.cityCenter.1') }}</li>
                  <li>{{ t('contact.directions.directions.cityCenter.2') }}</li>
                  <li>{{ t('contact.directions.directions.cityCenter.3') }}</li>
                </ul>
              </div>
            </div>
            
            <!-- Sincan, Eryaman Yönünden -->
            <div class="mb-4">
              <button 
                class="w-full px-5 py-3 flex justify-between items-center rounded-xl bg-gray-100 dark:bg-neutral-600 text-gray-700 dark:text-white font-medium hover:bg-primary/10 dark:hover:bg-primary/20 transition-all"
                @click="toggleAccordion('accordion2')"
                type="button">
                <span>{{ t('contact.directions.fromSincanEryaman') }}</span>
                <i class="fas fa-chevron-down transition-transform"
                   :class="{'rotate-180': openAccordion === 'accordion2'}"></i>
              </button>
              <div 
                class="overflow-hidden transition-all duration-300 bg-white dark:bg-neutral-700 rounded-b-xl"
                :class="{'max-h-0 mt-0': openAccordion !== 'accordion2', 'max-h-[500px] mt-2': openAccordion === 'accordion2'}">
                <ul class="p-5 space-y-2 text-gray-600 dark:text-gray-300 list-disc pl-5">
                  <li>{{ t('contact.directions.directions.sincanEryaman.0') }}</li>
                  <li>{{ t('contact.directions.directions.sincanEryaman.1') }}</li>
                  <li>{{ t('contact.directions.directions.sincanEryaman.2') }}</li>
                  <li>{{ t('contact.directions.directions.sincanEryaman.3') }}</li>
                  <li>{{ t('contact.directions.directions.sincanEryaman.4') }}</li>
                  <li>{{ t('contact.directions.directions.sincanEryaman.5') }}</li>
                </ul>
              </div>
            </div>
            
            <!-- Sincan, Eryaman Yönünden 2 -->
            <div class="mb-4">
              <button 
                class="w-full px-5 py-3 flex justify-between items-center rounded-xl bg-gray-100 dark:bg-neutral-600 text-gray-700 dark:text-white font-medium hover:bg-primary/10 dark:hover:bg-primary/20 transition-all"
                @click="toggleAccordion('accordion3')"
                type="button">
                <span>{{ t('contact.directions.fromSincanEryamanAlt') }}</span>
                <i class="fas fa-chevron-down transition-transform"
                   :class="{'rotate-180': openAccordion === 'accordion3'}"></i>
              </button>
              <div 
                class="overflow-hidden transition-all duration-300 bg-white dark:bg-neutral-700 rounded-b-xl"
                :class="{'max-h-0 mt-0': openAccordion !== 'accordion3', 'max-h-[500px] mt-2': openAccordion === 'accordion3'}">
                <ul class="p-5 space-y-2 text-gray-600 dark:text-gray-300 list-disc pl-5">
                  <li>{{ t('contact.directions.directions.sincanEryamanAlt.0') }}</li>
                  <li>{{ t('contact.directions.directions.sincanEryamanAlt.1') }}</li>
                  <li>{{ t('contact.directions.directions.sincanEryamanAlt.2') }}</li>
                  <li>{{ t('contact.directions.directions.sincanEryamanAlt.3') }}</li>
                  <li>{{ t('contact.directions.directions.sincanEryamanAlt.4') }}</li>
                  <li>{{ t('contact.directions.directions.sincanEryamanAlt.5') }}</li>
                </ul>
              </div>
            </div>
            
            <!-- Navigasyon Cihazı İle -->
            <div class="mb-4">
              <button 
                class="w-full px-5 py-3 flex justify-between items-center rounded-xl bg-gray-100 dark:bg-neutral-600 text-gray-700 dark:text-white font-medium hover:bg-primary/10 dark:hover:bg-primary/20 transition-all"
                @click="toggleAccordion('accordion4')"
                type="button">
                <span>{{ t('contact.directions.withNavigation') }}</span>
                <i class="fas fa-chevron-down transition-transform"
                   :class="{'rotate-180': openAccordion === 'accordion4'}"></i>
              </button>
              <div 
                class="overflow-hidden transition-all duration-300 bg-white dark:bg-neutral-700 rounded-b-xl"
                :class="{'max-h-0 mt-0': openAccordion !== 'accordion4', 'max-h-[500px] mt-2': openAccordion === 'accordion4'}">
                <ul class="p-5 space-y-2 text-gray-600 dark:text-gray-300 list-disc pl-5">
                  <li>{{ t('contact.directions.directions.withNavigation.0') }}</li>
                </ul>
              </div>
            </div>
            
            <div class="p-4 bg-primary/5 dark:bg-primary/10 rounded-xl mt-4">
              <p class="text-gray-700 dark:text-gray-300">
                <strong>{{ t('contact.directions.securityNote') }}</strong>
              </p>
            </div>
            
            <!-- Toplu Taşıma İle -->
            <div class="mt-6">
              <button 
                class="w-full px-5 py-3 flex justify-between items-center rounded-xl bg-gray-100 dark:bg-neutral-600 text-gray-700 dark:text-white font-medium hover:bg-primary/10 dark:hover:bg-primary/20 transition-all"
                @click="toggleAccordion('accordion5')"
                type="button">
                <span>{{ t('contact.directions.withPublicTransport') }}</span>
                <i class="fas fa-chevron-down transition-transform"
                   :class="{'rotate-180': openAccordion === 'accordion5'}"></i>
              </button>
              <div 
                class="overflow-hidden transition-all duration-300 bg-white dark:bg-neutral-700 rounded-b-xl"
                :class="{'max-h-0 mt-0': openAccordion !== 'accordion5', 'max-h-[1000px] mt-2': openAccordion === 'accordion5'}">
                <div class="p-5 space-y-4 text-gray-600 dark:text-gray-300">
                  <div>
                    <p class="font-medium mb-2">{{ t('contact.directions.publicTransport.option1.title') }}</p>
                    <p class="mb-2">{{ t('contact.directions.publicTransport.option1.description') }}</p>
                    
                    <div class="bg-gray-50 dark:bg-neutral-600 p-3 rounded-lg">
                      <p class="font-medium mb-1">{{ t('contact.directions.publicTransport.option1.weekday') }}</p>
                      <p class="text-sm">{{ t('contact.directions.publicTransport.option1.weekdayHours') }}</p>
                    </div>
                    
                    <div class="bg-gray-50 dark:bg-neutral-600 p-3 rounded-lg mt-2">
                      <p class="font-medium mb-1">{{ t('contact.directions.publicTransport.option1.saturday') }}</p>
                      <p class="text-sm">{{ t('contact.directions.publicTransport.option1.saturdayHours') }}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p class="font-medium mb-2">{{ t('contact.directions.publicTransport.option2.title') }}</p>
                    <p class="mb-2">{{ t('contact.directions.publicTransport.option2.description') }}</p>
                    
                    <div class="bg-gray-50 dark:bg-neutral-600 p-3 rounded-lg">
                      <p class="font-medium mb-1">{{ t('contact.directions.publicTransport.option2.weekday') }}</p>
                      <p class="text-sm">{{ t('contact.directions.publicTransport.option2.weekdayHours') }}</p>
                    </div>
                    
                    <div class="bg-gray-50 dark:bg-neutral-600 p-3 rounded-lg mt-2">
                      <p class="font-medium mb-1">{{ t('contact.directions.publicTransport.option2.saturday') }}</p>
                      <p class="text-sm">{{ t('contact.directions.publicTransport.option2.saturdayHours') }}</p>
                    </div>
                    
                    <div class="bg-primary/5 dark:bg-primary/10 p-3 rounded-lg mt-3">
                      <p>{{ t('contact.directions.publicTransport.option2.note') }}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p class="font-medium mb-2">{{ t('contact.directions.publicTransport.option3.title') }}</p>
                    <p class="bg-primary/5 dark:bg-primary/10 p-3 rounded-lg mt-2">
                      {{ t('contact.directions.publicTransport.option3.note') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Map Section -->
    <div class="h-[500px] relative">
      <div id="map" class="w-full h-full"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useContact } from '~/composables/useContact'
import { useSettings } from '~/composables/useSettings'
import { useAnalytics } from '~/composables/useAnalytics'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'

// i18n
const { t } = useI18n()

// Google Maps tipi tanımı
declare global {
  interface Window {
    google: any;
  }
}

// Toast bildirim
const toast = useToast()

// Site ayarlarını ve iletişim bilgilerini yükle
const { contactInfo, socialMedia, isLoading: settingsLoading } = useSettings()

// İletişim formu
const { 
  form,
  errors,
  isLoading,
  validateField, 
  submitForm 
} = useContact();

// Departman dropdown bileşeni için gereken değişkenler
const departmentOptions = [
  { value: 'general', label: t('contact.departments.general') },
  { value: 'sales', label: t('contact.departments.sales') },
  { value: 'support', label: t('contact.departments.support') },
  { value: 'hr', label: t('contact.departments.hr') },
  { value: 'press', label: t('contact.departments.press') }
];
const isOpenDepartment = ref(false);

const fileInput = ref<HTMLInputElement | null>(null);
const openAccordion = ref<string | null>(null);
const departmentDropdownRef = ref<HTMLElement | null>(null);

// Dropdown dışı tıklamayı izle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

function handleClickOutside(event: MouseEvent) {
  if (departmentDropdownRef.value && !departmentDropdownRef.value.contains(event.target as Node)) {
    isOpenDepartment.value = false;
  }
}

// Form gönderim işlemleri
function handleSubmit() {
  if (validateAllFields()) {
    submitContactForm();
  }
}

function validateAllFields() {
  const fields = ['name', 'email', 'department', 'subject', 'message'];
  let isValid = true;
  
  fields.forEach(field => {
    if (!validateField(field as any)) {
      isValid = false;
    }
  });
  
  // Telefon alanı girilmişse validasyon yap
  if (form.phone && !validateField('phone' as any)) {
    isValid = false;
  }
  
  return isValid;
}

async function submitContactForm() {
  try {
    const result = await submitForm();
    
    if (result) {
      toast.success(t('contact.successMessage'), {
        timeout: 5000
      });
    } else {
      toast.error(t('contact.errorMessage'), {
        timeout: 5000
      });
    }
  } catch (error) {
    console.error('Form submission error:', error);
    toast.error(t('contact.errorMessage'), {
      timeout: 5000
    });
  }
}

function handleFileUpload(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    form.file = target.files[0];
  }
}

function removeFile() {
  form.file = null;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

// Akordion durumunu değiştir
function toggleAccordion(id: string) {
  openAccordion.value = openAccordion.value === id ? null : id;
}

// Google Maps yükleme ve ayarlama
onMounted(() => {
  const script = document.createElement('script')
  script.src = `https://maps.googleapis.com/maps/api/js?key=YOUR_API_KEY`
  script.async = true
  script.defer = true
  script.onload = initMap
  document.head.appendChild(script)
})

const initMap = () => {
  const location = { lat: 40.123456, lng: 29.123456 } // Teknopark koordinatları
  const map = new window.google.maps.Map(document.getElementById('map') as HTMLElement, {
    center: location,
    zoom: 15,
    styles: [
      {
        featureType: "all",
        elementType: "geometry",
        stylers: [{"color": "#242f3e"}]
      },
      {
        featureType: "all",
        elementType: "labels.text.stroke",
        stylers: [{"lightness": -80}]
      },
      {
        featureType: "administrative",
        elementType: "labels.text.fill",
        stylers: [{"color": "#746855"}]
      },
      {
        featureType: "administrative.locality",
        elementType: "labels.text.fill",
        stylers: [{"color": "#d59563"}]
      },
      {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{"color": "#d59563"}]
      },
      {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{"color": "#263c3f"}]
      },
      {
        featureType: "poi.park",
        elementType: "labels.text.fill",
        stylers: [{"color": "#6b9a76"}]
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{"color": "#38414e"}]
      },
      {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{"color": "#212a37"}]
      },
      {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{"color": "#9ca5b3"}]
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{"color": "#746855"}]
      },
      {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{"color": "#1f2835"}]
      },
      {
        featureType: "road.highway",
        elementType: "labels.text.fill",
        stylers: [{"color": "#f3d19c"}]
      },
      {
        featureType: "transit",
        elementType: "geometry",
        stylers: [{"color": "#2f3948"}]
      },
      {
        featureType: "transit.station",
        elementType: "labels.text.fill",
        stylers: [{"color": "#d59563"}]
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{"color": "#17263c"}]
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{"color": "#515c6d"}]
      },
      {
        featureType: "water",
        elementType: "labels.text.stroke",
        stylers: [{"lightness": -20}]
      }
    ]
  })

  const marker = new window.google.maps.Marker({
    position: location,
    map: map,
    title: 'Teknopark İstanbul'
  })

  const infoWindow = new window.google.maps.InfoWindow({
    content: `
      <div class="p-2">
        <h3 class="font-bold mb-1">Teknopark İstanbul</h3>
        <p class="text-sm">Sanayi Mah. Teknopark Bulvarı<br>No:1/3A 34906<br>Pendik / İstanbul</p>
      </div>
    `
  })

  marker.addListener('click', () => {
    infoWindow.open(map, marker)
  })
}
</script>

<style>
/* Modern Form Stilleri */
.form-group {
  @apply relative mb-5;
}

.form-label {
  @apply block mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300;
}

.form-input {
  @apply w-full px-3.5 py-2.5 bg-white dark:bg-neutral-700 rounded-lg border-2 outline-none transition-all duration-300;
  @apply text-sm text-gray-900 dark:text-white;
  @apply border-gray-200 dark:border-neutral-600;
  @apply focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/20;
  @apply hover:border-gray-300 dark:hover:border-neutral-500;
  @apply placeholder-gray-400 dark:placeholder-gray-500;
}

.form-select {
  @apply w-full px-3.5 py-2.5 bg-white dark:bg-neutral-700 rounded-lg border-2 outline-none transition-all duration-300;
  @apply text-left text-sm text-gray-900 dark:text-white;
  @apply border-gray-200 dark:border-neutral-600;
  @apply hover:border-gray-300 dark:hover:border-neutral-500;
  @apply focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/20;
}

.dropdown-menu {
  @apply absolute z-10 w-full mt-1 bg-white dark:bg-neutral-700 rounded-lg shadow-lg;
  @apply border border-gray-200 dark:border-neutral-600;
  @apply animate-dropdown;
}

.dropdown-item {
  @apply w-full px-3.5 py-2.5 text-left text-sm transition-colors duration-200;
  @apply text-gray-900 dark:text-gray-100;
  @apply hover:bg-primary/5 hover:text-primary;
  @apply flex items-center;
}

.error-message {
  @apply text-xs text-red-500 mt-1.5;
}

/* Animasyonlar */
@keyframes dropdown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-dropdown {
  animation: dropdown 0.2s ease-out;
}

/* Custom Scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--primary-rgb), 0.2) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(var(--primary-rgb), 0.2);
  border-radius: 20px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(var(--primary-rgb), 0.3);
}
</style> 